import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  Theme,
  ThemeMode,
  ThemeContextValue,
  themes,
  getTheme,
  applyTheme,
  getStoredTheme,
  storeTheme,
} from "../themes";

// Create the context with a default value
const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// Theme Provider Props
type ThemeProviderProps = {
  children: React.ReactNode;
  defaultMode?: ThemeMode;
  enableTransitions?: boolean;
};

// Theme Provider Component
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultMode = "default",
  enableTransitions = true,
}) => {
  // Initialize theme mode from localStorage or default
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    return getStoredTheme() || defaultMode;
  });

  // Track available themes (allows for dynamic theme registration)
  const [availableThemes, setAvailableThemes] =
    useState<Record<ThemeMode, Theme>>(themes);

  // Get current theme object
  const theme = getTheme(themeMode);

  // Apply theme on mount and when it changes
  useEffect(() => {
    // Add transition class if enabled
    if (enableTransitions) {
      document.documentElement.style.transition = "all 0.3s ease-in-out";
    }

    // Apply the theme
    applyTheme(theme);

    // Store theme preference
    storeTheme(themeMode);

    // Cleanup transition after animation
    if (enableTransitions) {
      const timeout = setTimeout(() => {
        document.documentElement.style.transition = "";
      }, 300);
      return () => clearTimeout(timeout);
    }

    return;
  }, [themeMode, theme, enableTransitions]);

  // Set theme mode with validation
  const setThemeMode = useCallback(
    (mode: ThemeMode) => {
      if (mode in availableThemes) {
        setThemeModeState(mode);
      } else {
        console.warn(
          `Theme mode "${mode}" not found. Available themes:`,
          Object.keys(availableThemes),
        );
      }
    },
    [availableThemes],
  );

  // Register a new theme dynamically
  const registerTheme = useCallback((mode: ThemeMode, newTheme: Theme) => {
    setAvailableThemes((prev) => ({
      ...prev,
      [mode]: newTheme,
    }));
  }, []);

  // Context value
  const value: ThemeContextValue = {
    theme,
    themeMode,
    setThemeMode,
    availableThemes,
    registerTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
};

// Helper hook to get specific theme values
export const useThemeValue = <K extends keyof Theme>(key: K): Theme[K] => {
  const { theme } = useTheme();
  return theme[key];
};

// Helper hook to get theme colors
export const useThemeColors = () => {
  const { theme } = useTheme();
  return theme.colors;
};

// Helper hook to get theme typography
export const useThemeTypography = () => {
  const { theme } = useTheme();
  return theme.typography;
};

// Helper hook to check if current theme is dark
export const useIsDarkTheme = (): boolean => {
  const { theme } = useTheme();
  return theme.isDark;
};

// Helper hook for theme-aware className generation
export const useThemeClassName = (
  baseClass: string,
  themeVariants?: Record<ThemeMode, string>,
): string => {
  const { themeMode } = useTheme();

  if (themeVariants && themeMode in themeVariants) {
    return `${baseClass} ${themeVariants[themeMode]}`;
  }

  return baseClass;
};
